import { useState } from 'react';
import Header from '../layout/Header';
import { motion } from 'framer-motion';
import { Inbox, Edit3, CheckCircle2, ArrowRightLeft, FolderKanban, MapPinned, Brain, Cpu, Receipt } from 'lucide-react';
import GouvernanceGestionProjetsModal from '../components/GouvernanceGestionProjetsModal';
import MachineIAModal from '../components/MachineIAModal';
import GouvernanceComptesAIOAuthRevisionModal from '../components/GouvernanceComptesAIOAuthRevisionModal';
import PolitiqueFacturationClientsRevisionModal from '../components/PolitiqueFacturationClientsRevisionModal';

const WORKFLOW_STEPS = [
  {
    id: 1,
    title: 'Nouveau contenu ajouté',
    icon: Inbox,
    description:
      'Une idée, un modèle, un standard ou une structure PRIsME est proposé par un membre de léquipe (document, gabarit, carte, visuel, etc.).',
  },
  {
    id: 2,
    title: 'Contenu en révision',
    icon: Edit3,
    description:
      'Le contenu est analysé, challengé et bonifié. On vérifie son alignement avec PRIsME, les autres standards BZ et la séparation avec TactiCE.',
  },
  {
    id: 3,
    title: 'Validation / approbation',
    icon: CheckCircle2,
    description:
      'Les responsables PRIsME concernés approuvent officiellement le contenu (gouvernance, standards, modèles, structures).',
  },
  {
    id: 4,
    title: 'Intégration PRIsME officielle',
    icon: ArrowRightLeft,
    description:
      'Le contenu validé est déplacé et intégré dans la bonne section PRIsME (page département, concept, gouvernance, etc.) et devient source de vérité officielle.',
  },
];

export default function ContenuEnRevision() {
  const [isGestionProjetsOpen, setIsGestionProjetsOpen] = useState(false);
  const [isMachineIAOpen, setIsMachineIAOpen] = useState(false);
  const [isGouvernanceComptesAIOAuthOpen, setIsGouvernanceComptesAIOAuthOpen] = useState(false);
  const [isPolitiqueFacturationOpen, setIsPolitiqueFacturationOpen] = useState(false);

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-50 to-gray-100">
      <Header
        title="Contenu en révision"
        subtitle="Zone tampon éditoriale pour le contenu PRIsME non encore officiel — avant intégration dans les pages standards."
      />

      <div className="max-w-7xl mx-auto px-8 pb-12 space-y-12">
        {/* Intro éditoriale */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Pourquoi une section « Contenu en révision » ?</h2>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              PRIsME définit la gouvernance, les standards, les modèles et les structures. Pour protéger cette cohérence,
              tout nouveau contenu doit d&apos;abord passer par une zone tampon éditoriale avant d&apos;être intégré dans les
              pages officielles.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              Cette page sert de sas de validation : on y dépose les nouvelles idées, les gabarits de documents, les
              propositions de cartes `.tsx` et les structures en cours de définition. Tant qu&apos;un élément reste dans
              « Contenu en révision », il n&apos;est pas considéré comme standard PRIsME officiel.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Une fois validé, le contenu est déplacé dans la bonne page PRIsME (Direction TI, Cybersécurité, Gestion
              documentaire, Concepts, etc.) et devient alors la source de vérité pour l&apos;équipe et les clients.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-6">
            <h3 className="text-sm font-semibold text-amber-800 mb-2 uppercase tracking-wide">
              Rappel — frontière PRIsME / TactiCE
            </h3>
            <p className="text-xs text-amber-900 leading-relaxed">
              PRIsME documente la gouvernance, les standards et les modèles.
              <br />
              TactiCE exécute les opérations.
              <br />
              <br />
              Le contenu en révision doit rester au niveau référentiel (concepts, structures, modèles), sans logique
              opérationnelle TactiCE. La section ci-dessous sert uniquement à préparer le contenu avant son intégration
              dans les pages PRIsME officielles.
            </p>
          </div>
        </section>

        {/* Workflow d'autorisation sous forme de cards */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-linear-to-r from-gray-300 to-gray-200" />
            <h2 className="text-2xl font-bold text-gray-800 px-4">Workflow de validation du contenu</h2>
            <div className="flex-1 h-px bg-linear-to-l from-gray-300 to-gray-200" />
          </div>

          <p className="text-sm text-gray-700 mb-6 max-w-3xl">
            Le cycle de vie éditorial est simple : on reçoit une proposition, on la révise, on l&apos;approuve, puis on
            l&apos;intègre dans la bonne section PRIsME. Les étapes ci-dessous serviront plus tard de base pour des cartes
            interactives et des modals détaillés.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WORKFLOW_STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col h-full"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-bz-blue/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-bz-blue" />
                    </div>
                    <span className="text-xs font-semibold text-gray-400">Étape {step.id}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-xs text-gray-700 leading-relaxed flex-1">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Zone active pour les cards PRIsME en révision */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-linear-to-r from-bz-blue/40 to-transparent" />
            <h2 className="text-lg font-semibold text-gray-800 px-4">Cards PRIsME actuellement en révision</h2>
            <div className="flex-1 h-px bg-linear-to-l from-bz-blue/40 to-transparent" />
          </div>

          <p className="text-xs text-gray-600 mb-5 max-w-3xl">
            Toute nouvelle card PRIsME doit naître ici. Tant qu&apos;elle demeure dans cette page, elle est considérée
            comme un contenu en attente de validation humaine. La destination finale visée peut être indiquée,
            mais aucun déplacement vers un département PRIsME ne doit être fait avant approbation.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            <motion.button
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.015, y: -4 }}
              onClick={() => setIsGestionProjetsOpen(true)}
              className="relative overflow-hidden rounded-3xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-72 group border border-violet-200 bg-white text-left"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-violet-800 to-indigo-700 opacity-95 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M50 50V30H30v20h20zm0-30V0H30v20h20zM20 50V30H0v20h20zm60 0V30H60v20h20zM20 80V60H0v20h20zm30 0V60H30v20h20zm30 0V60H60v20h20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }} />

              <div className="relative h-full p-6 flex flex-col justify-between text-white">
                <div>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-inner">
                      <FolderKanban className="w-6 h-6 text-white" />
                    </div>
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-violet-300/40 bg-violet-500/20 px-3 py-1 text-[11px] font-semibold text-violet-100">
                      <Brain className="w-3.5 h-3.5" />
                      En révision
                    </div>
                  </div>

                  <h3 className="text-xl font-black drop-shadow-sm mb-2">Gouvernance - Gestion de projets</h3>
                  <p className="text-sm text-white/85 leading-relaxed mb-4">
                    Cadre riche pour coacher les gestionnaires, structurer les rôles, outiller les comités et faire de la gestion de projets une vraie discipline de gouvernance PRIsME.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="rounded-2xl bg-white/10 border border-white/10 p-3 text-xs text-white/90">
                    <span className="font-semibold text-white">Destination suggérée :</span> Direction TI
                  </div>
                  <div className="flex items-center justify-between text-xs text-white/80">
                    <div className="inline-flex items-center gap-2">
                      <MapPinned className="w-4 h-4" />
                      Contenu non officiel tant que non validé
                    </div>
                    <span className="font-semibold text-white">Ouvrir la card →</span>
                  </div>
                </div>
              </div>
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.015, y: -4 }}
              onClick={() => setIsGouvernanceComptesAIOAuthOpen(true)}
              className="relative overflow-hidden rounded-3xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-72 group border border-indigo-200 bg-white text-left"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-800 to-blue-700 opacity-95 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative h-full p-6 flex flex-col justify-between text-white">
                <div>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-inner">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300/40 bg-indigo-500/20 px-3 py-1 text-[11px] font-semibold text-indigo-100">
                      <Brain className="w-3.5 h-3.5" />
                      En révision
                    </div>
                  </div>

                  <h3 className="text-xl font-black drop-shadow-sm mb-2">Gouvernance des comptes AI & modèle OAuth Agentic (AIKUB)</h3>
                  <p className="text-sm text-white/85 leading-relaxed mb-4">
                    Cadre Master pour standardiser le modèle 1 agent = 1 humain = 1 compte OAuth, avec ownership clair, BotOps central, coût fixe et scaling horizontal.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="rounded-2xl bg-white/10 border border-white/10 p-3 text-xs text-white/90">
                    <span className="font-semibold text-white">Destination suggérée :</span> Programmation & IA, section Gouvernance
                  </div>
                  <div className="flex items-center justify-between text-xs text-white/80">
                    <div className="inline-flex items-center gap-2">
                      <MapPinned className="w-4 h-4" />
                      Statut éditorial: contenu en révision
                    </div>
                    <span className="font-semibold text-white">Ouvrir la card →</span>
                  </div>
                </div>
              </div>
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.015, y: -4 }}
              onClick={() => setIsMachineIAOpen(true)}
              className="relative overflow-hidden rounded-3xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-72 group border border-fuchsia-200 bg-white text-left"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-fuchsia-800 to-purple-700 opacity-95 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative h-full p-6 flex flex-col justify-between text-white">
                <div>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-inner">
                      <Cpu className="w-6 h-6 text-white" />
                    </div>
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-fuchsia-300/40 bg-fuchsia-500/20 px-3 py-1 text-[11px] font-semibold text-fuchsia-100">
                      <Brain className="w-3.5 h-3.5" />
                      En révision
                    </div>
                  </div>

                  <h3 className="text-xl font-black drop-shadow-sm mb-2">Machine IA</h3>
                  <p className="text-sm text-white/85 leading-relaxed mb-4">
                    Comparatif de 3 scénarios d’achat pour une machine dédiée à l’IA, avec VRAM, compatibilité logicielle, modèles LLM, ECC, évolutivité et références.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="rounded-2xl bg-white/10 border border-white/10 p-3 text-xs text-white/90">
                    <span className="font-semibold text-white">Destination suggérée :</span> Programmation & IA
                  </div>
                  <div className="flex items-center justify-between text-xs text-white/80">
                    <div className="inline-flex items-center gap-2">
                      <MapPinned className="w-4 h-4" />
                      Contenu non officiel tant que non validé
                    </div>
                    <span className="font-semibold text-white">Ouvrir la card →</span>
                  </div>
                </div>
              </div>
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.015, y: -4 }}
              onClick={() => setIsPolitiqueFacturationOpen(true)}
              className="relative overflow-hidden rounded-3xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-72 group border border-emerald-200 bg-white text-left"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-emerald-800 to-teal-700 opacity-95 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative h-full p-6 flex flex-col justify-between text-white">
                <div>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-inner">
                      <Receipt className="w-6 h-6 text-white" />
                    </div>
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/40 bg-emerald-500/20 px-3 py-1 text-[11px] font-semibold text-emerald-100">
                      <Brain className="w-3.5 h-3.5" />
                      En révision
                    </div>
                  </div>

                  <h3 className="text-xl font-black drop-shadow-sm mb-2">Politique de facturation clients & crédits</h3>
                  <p className="text-sm text-white/85 leading-relaxed mb-4">
                    Brouillon de politique interne pour standardiser la facturation des contrats, tickets et matériels, ainsi que le traitement des crédits clients.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="rounded-2xl bg-white/10 border border-white/10 p-3 text-xs text-white/90">
                    <span className="font-semibold text-white">Destination suggérée :</span> Administration & RH
                  </div>
                  <div className="flex items-center justify-between text-xs text-white/80">
                    <div className="inline-flex items-center gap-2">
                      <MapPinned className="w-4 h-4" />
                      Liée à Recevables et Demande de Crédits
                    </div>
                    <span className="font-semibold text-white">Ouvrir la card →</span>
                  </div>
                </div>
              </div>
            </motion.button>
            <div className="border border-dashed border-gray-300 rounded-2xl p-5 bg-white/40 text-xs text-gray-500">
              Emplacement réservé pour les prochaines cartes de{' '}
              <span className="font-semibold text-gray-700">modèles / structures</span> en attente de validation.
            </div>
          </div>
        </section>
      </div>

      <GouvernanceGestionProjetsModal
        isOpen={isGestionProjetsOpen}
        onClose={() => setIsGestionProjetsOpen(false)}
      />

      <MachineIAModal
        isOpen={isMachineIAOpen}
        onClose={() => setIsMachineIAOpen(false)}
      />

      <GouvernanceComptesAIOAuthRevisionModal
        isOpen={isGouvernanceComptesAIOAuthOpen}
        onClose={() => setIsGouvernanceComptesAIOAuthOpen(false)}
      />

      <PolitiqueFacturationClientsRevisionModal
        isOpen={isPolitiqueFacturationOpen}
        onClose={() => setIsPolitiqueFacturationOpen(false)}
      />

    </div>
  );
}
